
import React, { useEffect } from 'react';
import { SiteTheme } from '../../types';
import { generateThemeStyles } from '../dashboard/cms/CMSConstants';

interface ThemeManagerProps {
    theme: SiteTheme;
    rootRef?: React.RefObject<HTMLElement | null>;
}

export const ThemeManager: React.FC<ThemeManagerProps> = ({ theme, rootRef }) => {
    useEffect(() => {
        // Om rootRef finns, använd den containern. Annars fallback till document.documentElement (globalt)
        const target = rootRef?.current || document.documentElement;
        if (!theme || !target) return;

        // 1. Apply Color Theme
        target.style.setProperty('--color-primary', theme.primary);
        target.style.setProperty('--color-secondary', theme.secondary);
        target.style.setProperty('--color-accent', theme.accent);
        target.style.setProperty('--color-bg-main', theme.background);
        target.style.setProperty('--color-bg-surface', theme.surface);

        // Chrome-toppraden (theme-color) ska matcha sidans bakgrund — men bara
        // globalt. En scopad instans (rootRef, t.ex. CMS-förhandsgranskning)
        // ska inte styra den riktiga webbläsarkromen.
        if (!rootRef && theme.background) {
            document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.background);
        }

        // 2. Apply Dynamic Style Mode (Fonts, Radius, Glass)
        const styles = generateThemeStyles(theme);
        
        Object.entries(styles).forEach(([key, value]) => {
            target.style.setProperty(key, value as string);
        });

        // Cleanup function
        return () => {
            // Rensa endast om vi applicerade på global root (för att undvika sidoeffekter vid unmount)
            // Om vi scopar behöver vi inte strikt rensa då containern ofta försvinner, men det är god sed.
            if (!rootRef) {
                target.style.removeProperty('--color-primary');
                target.style.removeProperty('--color-secondary');
                target.style.removeProperty('--color-accent');
                target.style.removeProperty('--color-bg-main');
                target.style.removeProperty('--color-bg-surface');
                
                Object.keys(styles).forEach((key) => {
                    target.style.removeProperty(key);
                });
            } else {
                // För scoped element, rensa attributet style helt om det är enklare, eller specifika props
                // Här låter vi det vara då komponenten oftast unmountas med sin container.
            }
        };

    }, [theme, rootRef]);

    return null; 
};
